import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressMembersCustomfieldsApplicationsCustomFieldApplicationsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/members/v1/custom-fields-applications',
        destPath: '/v1/custom-fields-applications',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/members/v1/custom-fields-applications',
        destPath: '/v1/custom-fields-applications',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/members/v1/custom-fields-applications',
        destPath: '/v1/custom-fields-applications',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/members/v1/custom-fields-applications',
        destPath: '/v1/custom-fields-applications',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/members/v1/custom-fields-applications',
        destPath: '/v1/custom-fields-applications',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_members_custom-field-applications';

/** Creates a custom field application. */
export function createCustomFieldApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __createCustomFieldApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field_application',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.customfields.applications.CustomFieldApplications.CreateCustomFieldApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsApplicationsCustomFieldApplicationsUrl(
        { protoPath: '/v1/custom-fields-applications', data: payload, host }
      ),
      data: payload,
    };

    return metadata;
  }

  return __createCustomFieldApplication;
}

/**
 * Deletes a custom field application.
 *
 * When you delete the application, the field is automatically applied to all members.
 */
export function deleteCustomFieldApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteCustomFieldApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field_application',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.members.customfields.applications.CustomFieldApplications.DeleteCustomFieldApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsApplicationsCustomFieldApplicationsUrl(
        {
          protoPath: '/v1/custom-fields-applications/{customFieldId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCustomFieldApplication;
}

/**
 * Updates a custom field application.
 *
 * Partial updates are not supported. Pass the whole `application` object in the request.
 */
export function updateCustomFieldApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __updateCustomFieldApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field_application',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.members.customfields.applications.CustomFieldApplications.UpdateCustomFieldApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsApplicationsCustomFieldApplicationsUrl(
        {
          protoPath:
            '/v1/custom-fields-applications/{application.customFieldId}',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __updateCustomFieldApplication;
}

/** Retrieves a custom field application. */
export function getCustomFieldApplication(
  payload: object
): RequestOptionsFactory<any> {
  function __getCustomFieldApplication({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field_application',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.members.customfields.applications.CustomFieldApplications.GetCustomFieldApplication',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsApplicationsCustomFieldApplicationsUrl(
        {
          protoPath: '/v1/custom-fields-applications/{customFieldId}',
          data: payload,
          host,
        }
      ),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getCustomFieldApplication;
}

/** Retrieves a list of custom field applications. */
export function getCustomFieldApplications(
  payload: object
): RequestOptionsFactory<any> {
  function __getCustomFieldApplications({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field_application',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.customfields.applications.CustomFieldApplications.GetCustomFieldApplications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsApplicationsCustomFieldApplicationsUrl(
        {
          protoPath: '/v1/custom-fields-applications/applications',
          data: payload,
          host,
        }
      ),
      data: payload,
    };

    return metadata;
  }

  return __getCustomFieldApplications;
}

/** Retrieves a list of fields that are applied to specified members. */
export function getMembersCustomFieldApplications(
  payload: object
): RequestOptionsFactory<any> {
  function __getMembersCustomFieldApplications({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field_application',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.customfields.applications.CustomFieldApplications.GetMembersCustomFieldApplications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsApplicationsCustomFieldApplicationsUrl(
        {
          protoPath: '/v1/custom-fields-applications/members',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.applications.customField.createdDate' },
              { path: 'results.applications.customField.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getMembersCustomFieldApplications;
}

/** Retrieves a list of fields that are applied to specified roles. */
export function getRolesCustomFieldApplications(
  payload: object
): RequestOptionsFactory<any> {
  function __getRolesCustomFieldApplications({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field_application',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.customfields.applications.CustomFieldApplications.GetRolesCustomFieldApplications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsApplicationsCustomFieldApplicationsUrl(
        {
          protoPath: '/v1/custom-fields-applications/roles',
          data: payload,
          host,
        }
      ),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.applications.customField.createdDate' },
              { path: 'results.applications.customField.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getRolesCustomFieldApplications;
}
